<template>
  <div class="d-flex align-center dashboard-date-filter">
    <v-select
      :value="preset"
      @change="onPresetChange"
      :items="presetOptions"
      dense
      outlined
      hide-details
      class="preset-select mr-2"
    ></v-select>
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      min-width="auto"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          :value="rangeText"
          readonly
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-calendar"
          class="range-display"
          v-bind="attrs"
          v-on="on"
        ></v-text-field>
      </template>
      <v-date-picker
        v-model="pickerRange"
        range
        no-title
        @change="onCustomRangeChange"
      ></v-date-picker>
    </v-menu>
  </div>
</template>

<script>
export default {
  name: 'DashboardDateFilter',
  props: {
    value: {
      type: Object,
      default: () => ({ preset: 'this_month', start_date: null, end_date: null }),
    },
  },
  data() {
    return {
      menu: false,
      pickerRange: [],
      presetOptions: [
        { text: 'Today', value: 'today' },
        { text: 'This Week', value: 'this_week' },
        { text: 'This Month', value: 'this_month' },
        { text: 'Last Month', value: 'last_month' },
        { text: 'Last 7 Days', value: 'last_7_days' },
        { text: 'Last 30 Days', value: 'last_30_days' },
        { text: 'All Time', value: 'all' },
        { text: 'Custom Range', value: 'custom' },
      ],
    };
  },
  computed: {
    preset() { return this.value.preset; },
    rangeText() {
      if (this.value.preset === 'all') return 'All Time';
      if (!this.value.start_date) return '';
      const start = this.formatDisplay(this.value.start_date);
      const end = this.formatDisplay(this.value.end_date || this.value.start_date);
      return start === end ? start : `${start} – ${end}`;
    },
  },
  mounted() {
    if (!this.value.start_date && this.value.preset !== 'all') {
      this.emitResolved(this.value.preset || 'this_month');
    }
  },
  methods: {
    formatDisplay(dateStr) {
      if (!dateStr) return '';
      const [y, m, d] = dateStr.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
    },
    todayParts() {
      const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
      const [y, m, d] = todayStr.split('-').map(Number);
      return { y, m, d };
    },
    addDaysUTC(y, m, d, delta) {
      const dt = new Date(Date.UTC(y, m - 1, d));
      dt.setUTCDate(dt.getUTCDate() + delta);
      return dt;
    },
    fmtUTC(dt) { return dt.toISOString().slice(0, 10); },
    resolvePreset(preset) {
      const { y, m, d } = this.todayParts();
      const today = new Date(Date.UTC(y, m - 1, d));
      switch (preset) {
        case 'today':
          return { start_date: this.fmtUTC(today), end_date: this.fmtUTC(today) };
        case 'this_week': {
          const dow = today.getUTCDay(); // 0=Sun..6=Sat
          const mondayOffset = dow === 0 ? -6 : 1 - dow;
          const monday = this.addDaysUTC(y, m, d, mondayOffset);
          const sunday = this.addDaysUTC(monday.getUTCFullYear(), monday.getUTCMonth() + 1, monday.getUTCDate(), 6);
          return { start_date: this.fmtUTC(monday), end_date: this.fmtUTC(sunday) };
        }
        case 'this_month': {
          const first = new Date(Date.UTC(y, m - 1, 1));
          const last = new Date(Date.UTC(y, m, 0));
          return { start_date: this.fmtUTC(first), end_date: this.fmtUTC(last) };
        }
        case 'last_month': {
          const first = new Date(Date.UTC(y, m - 2, 1));
          const last = new Date(Date.UTC(y, m - 1, 0));
          return { start_date: this.fmtUTC(first), end_date: this.fmtUTC(last) };
        }
        case 'last_7_days':
          return { start_date: this.fmtUTC(this.addDaysUTC(y, m, d, -6)), end_date: this.fmtUTC(today) };
        case 'last_30_days':
          return { start_date: this.fmtUTC(this.addDaysUTC(y, m, d, -29)), end_date: this.fmtUTC(today) };
        case 'all':
        default:
          return { start_date: null, end_date: null };
      }
    },
    emitResolved(preset) {
      const { start_date, end_date } = this.resolvePreset(preset);
      const resolved = { preset, start_date, end_date };
      this.$emit('input', resolved);
      this.$emit('change', resolved);
    },
    onPresetChange(preset) {
      this.menu = false;
      if (preset === 'custom') {
        this.pickerRange = this.value.start_date ? [this.value.start_date, this.value.end_date || this.value.start_date] : [];
        this.menu = true;
        return;
      }
      this.emitResolved(preset);
    },
    onCustomRangeChange(range) {
      if (!range || range.length < 2) return;
      const [a, b] = range;
      const start_date = a <= b ? a : b;
      const end_date = a <= b ? b : a;
      this.menu = false;
      const resolved = { preset: 'custom', start_date, end_date };
      this.$emit('input', resolved);
      this.$emit('change', resolved);
    },
  },
};
</script>

<style scoped>
.dashboard-date-filter { flex-wrap: wrap; }
.preset-select { max-width: 160px; }
.range-display { max-width: 230px; }
</style>
